import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import ImageExtension from '@tiptap/extension-image'
import { marked } from 'marked'
import TurndownService from 'turndown'
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Code,
  Link as LinkIcon,
  Undo2,
  Redo2,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Minus,
  Table as TableIcon,
  Image,
  FileText,
  Info,
} from 'lucide-react'

const turndown = new TurndownService({ headingStyle: 'atx', codeBlockStyle: 'fenced' })

export interface WikiEditorHandle {
  insertMarkdown: (snippet: string) => void
}

interface WikiEditorProps {
  initialMarkdown: string
  onChange: (markdown: string) => void
  readOnly?: boolean
}

function markdownToHtml(md: string): string {
  return marked.parse(md, { async: false }) as string
}

const WikiEditor = forwardRef<WikiEditorHandle, WikiEditorProps>(function WikiEditor(
  { initialMarkdown, onChange, readOnly = false },
  ref
) {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const [rawMode, setRawMode] = useState(false)
  const [rawContent, setRawContent] = useState('')
  const [showTableMenu, setShowTableMenu] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const tableMenuRef = useRef<HTMLDivElement>(null)
  const infoRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tableMenuRef.current && !tableMenuRef.current.contains(e.target as Node)) {
        setShowTableMenu(false)
      }
      if (infoRef.current && !infoRef.current.contains(e.target as Node)) {
        setShowInfo(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-primary-400 underline' } }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      ImageExtension.configure({ inline: false, allowBase64: false }),
    ],
    content: markdownToHtml(initialMarkdown),
    editable: !readOnly,
    editorProps: {
      attributes: {
        class:
          'prose-invert prose-sm max-w-none min-h-[20rem] px-4 py-3 focus:outline-none text-gray-100 [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg [&_a]:text-primary-400 [&_code]:bg-gray-800 [&_code]:px-1 [&_code]:rounded [&_pre]:bg-gray-800 [&_pre]:p-3 [&_pre]:rounded-lg [&_blockquote]:border-l-4 [&_blockquote]:border-gray-600 [&_blockquote]:pl-4 [&_blockquote]:italic [&_hr]:border-gray-700 [&_hr]:my-4 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-gray-600 [&_th]:p-2 [&_th]:bg-gray-800 [&_th]:font-semibold [&_td]:border [&_td]:border-gray-600 [&_td]:p-2 [&_img]:max-w-full [&_img]:rounded-lg [&_img]:my-2',
      },
    },
    onUpdate: ({ editor: ed }) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        const md = turndown.turndown(ed.getHTML())
        onChangeRef.current(md)
      }, 300)
    },
  })

  useEffect(() => {
    if (!editor) return
    editor.setEditable(!readOnly)
  }, [editor, readOnly])

  useEffect(() => {
    if (!editor) return
    const html = markdownToHtml(initialMarkdown)
    const current = editor.getHTML()
    const currentMd = turndown.turndown(current)
    if (currentMd !== initialMarkdown) {
      editor.commands.setContent(html, { emitUpdate: false })
    }
  }, [editor, initialMarkdown])

  useImperativeHandle(ref, () => ({
    insertMarkdown(snippet: string) {
      if (!editor) return
      const html = markdownToHtml(snippet)
      ch().insertContent(html).run()
    },
  }))

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  if (!editor) return null

  // tiptap 3.x chain helper — bypass strict chain types
  const ch = () => (editor.chain().focus() as any) as { [key: string]: (...args: any[]) => any } & { run: () => boolean }
  const cn = (method: string) => (editor.can() as any)[method] ?? false

  const btnClass =
    'inline-flex items-center justify-center min-h-[44px] min-w-[44px] px-2 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-gray-100 disabled:opacity-40'

  const dropdownItemClass =
    'w-full text-left px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-gray-100'

  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Link URL', prev ?? 'https://')
    if (url === null) return
    if (url === '') {
      ch().extendMarkRange('link').unsetLink().run()
      return
    }
    ch().extendMarkRange('link').setLink({ href: url }).run()
  }

  const setImage = () => {
    const url = window.prompt('Image URL')
    if (url) {
      ch().setImage({ src: url }).run()
    }
  }

  const toggleRawMode = () => {
    if (!editor) return
    if (rawMode) {
      const md = textareaRef.current?.value ?? ''
      if (md.trim()) {
        const html = markdownToHtml(md)
        editor.commands.setContent(html, { emitUpdate: false })
        onChangeRef.current(md)
      }
      setRawMode(false)
    } else {
      const html = editor.getHTML()
      const md = turndown.turndown(html)
      setRawContent(md)
      setRawMode(true)
    }
  }

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900 overflow-hidden">
      {!readOnly && (
        <div className="flex flex-wrap gap-1 p-2 border-b border-gray-700 bg-gray-900/80">
          {!rawMode && (
            <>
              <button
                type="button"
                className={btnClass}
                onClick={() => ch().toggleHeading({ level: 1 }).run()}
                title="Heading 1"
              >
                <Heading1 className="w-4 h-4" />
              </button>
              <button
                type="button"
                className={btnClass}
                onClick={() => ch().toggleHeading({ level: 2 }).run()}
                title="Heading 2"
              >
                <Heading2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                className={btnClass}
                onClick={() => ch().toggleHeading({ level: 3 }).run()}
                title="Heading 3"
              >
                <Heading3 className="w-4 h-4" />
              </button>
              <span className="w-px h-8 bg-gray-700 self-center mx-1" />
              <button
                type="button"
                className={btnClass}
                onClick={() => ch().toggleBold().run()}
                title="Bold"
              >
                <Bold className="w-4 h-4" />
              </button>
              <button
                type="button"
                className={btnClass}
                onClick={() => ch().toggleItalic().run()}
                title="Italic"
              >
                <Italic className="w-4 h-4" />
              </button>
              <button
                type="button"
                className={btnClass}
                onClick={() => ch().toggleBulletList().run()}
                title="Bullet list"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                type="button"
                className={btnClass}
                onClick={() => ch().toggleOrderedList().run()}
                title="Ordered list"
              >
                <ListOrdered className="w-4 h-4" />
              </button>
              <button
                type="button"
                className={btnClass}
                onClick={() => ch().toggleCodeBlock().run()}
                title="Code block"
              >
                <Code className="w-4 h-4" />
              </button>
              <button
                type="button"
                className={btnClass}
                onClick={() => ch().toggleBlockquote().run()}
                title="Blockquote"
              >
                <Quote className="w-4 h-4" />
              </button>
              <button type="button" className={btnClass} onClick={setLink} title="Link">
                <LinkIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                className={btnClass}
                onClick={() => ch().setHorizontalRule().run()}
                title="Horizontal rule"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-px h-8 bg-gray-700 self-center mx-1" />
              <div className="relative" ref={tableMenuRef}>
                <button
                  type="button"
                  className={btnClass}
                  onClick={() => setShowTableMenu((v) => !v)}
                  title="Table"
                >
                  <TableIcon className="w-4 h-4" />
                </button>
                {showTableMenu && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50 py-1">
                    <button
                      type="button"
                      className={dropdownItemClass}
                      onClick={() => {
                        ch().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
                        setShowTableMenu(false)
                      }}
                    >
                      Insert Table
                    </button>
                    <button
                      type="button"
                      className={dropdownItemClass}
                      onClick={() => {
                        ch().addColumnBefore().run()
                        setShowTableMenu(false)
                      }}
                    >
                      Add Column Before
                    </button>
                    <button
                      type="button"
                      className={dropdownItemClass}
                      onClick={() => {
                        ch().addColumnAfter().run()
                        setShowTableMenu(false)
                      }}
                    >
                      Add Column After
                    </button>
                    <button
                      type="button"
                      className={dropdownItemClass}
                      onClick={() => {
                        ch().addRowBefore().run()
                        setShowTableMenu(false)
                      }}
                    >
                      Add Row Before
                    </button>
                    <button
                      type="button"
                      className={dropdownItemClass}
                      onClick={() => {
                        ch().addRowAfter().run()
                        setShowTableMenu(false)
                      }}
                    >
                      Add Row After
                    </button>
                    <div className="border-t border-gray-700 my-1" />
                    <button
                      type="button"
                      className={dropdownItemClass}
                      onClick={() => {
                        ch().deleteRow().run()
                        setShowTableMenu(false)
                      }}
                    >
                      Delete Row
                    </button>
                    <button
                      type="button"
                      className={dropdownItemClass}
                      onClick={() => {
                        ch().deleteColumn().run()
                        setShowTableMenu(false)
                      }}
                    >
                      Delete Column
                    </button>
                    <button
                      type="button"
                      className={dropdownItemClass}
                      onClick={() => {
                        ch().deleteTable().run()
                        setShowTableMenu(false)
                      }}
                    >
                      Delete Table
                    </button>
                  </div>
                )}
              </div>
              <button type="button" className={btnClass} onClick={setImage} title="Insert image">
                <Image className="w-4 h-4" />
              </button>
              <span className="w-px h-8 bg-gray-700 self-center mx-1" />
              <button
                type="button"
                className={btnClass}
                disabled={!cn('undo')}
                onClick={() => ch().undo().run()}
                title="Undo"
              >
                <Undo2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                className={btnClass}
                disabled={!cn('redo')}
                onClick={() => ch().redo().run()}
                title="Redo"
              >
                <Redo2 className="w-4 h-4" />
              </button>
            </>
          )}
          <span className="w-px h-8 bg-gray-700 self-center mx-1" />
          <button
            type="button"
            className={`${btnClass}${rawMode ? ' bg-gray-700 text-gray-100' : ''}`}
            onClick={toggleRawMode}
            title="Toggle raw markdown"
          >
            <FileText className="w-4 h-4" />
          </button>
          <div className="relative" ref={infoRef}>
            <button
              type="button"
              className={btnClass}
              onClick={() => setShowInfo((v) => !v)}
              title="Keyboard shortcuts"
            >
              <Info className="w-4 h-4" />
            </button>
            {showInfo && (
              <div className="absolute top-full right-0 mt-1 w-72 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50 p-4">
                <h4 className="text-sm font-semibold text-gray-200 mb-3">Keyboard Shortcuts</h4>
                <div className="space-y-1.5 text-xs text-gray-400">
                  <div className="flex justify-between items-center">
                    <span>Bold</span>
                    <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-300 font-mono text-[10px]">Ctrl+B</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Italic</span>
                    <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-300 font-mono text-[10px]">Ctrl+I</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Heading 1</span>
                    <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-300 font-mono text-[10px]">Ctrl+Alt+1</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Heading 2</span>
                    <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-300 font-mono text-[10px]">Ctrl+Alt+2</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Heading 3</span>
                    <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-300 font-mono text-[10px]">Ctrl+Alt+3</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Bullet List</span>
                    <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-300 font-mono text-[10px]">Ctrl+Shift+8</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Ordered List</span>
                    <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-300 font-mono text-[10px]">Ctrl+Shift+7</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Blockquote</span>
                    <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-300 font-mono text-[10px]">Ctrl+Shift+&gt;</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Code Block</span>
                    <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-300 font-mono text-[10px]">Ctrl+Alt+C</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Link</span>
                    <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-300 font-mono text-[10px]">Ctrl+K</kbd>
                  </div>
                  <div className="border-t border-gray-700 my-2" />
                  <div className="flex justify-between items-center">
                    <span>Undo</span>
                    <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-300 font-mono text-[10px]">Ctrl+Z</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Redo</span>
                    <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-300 font-mono text-[10px]">Ctrl+Shift+Z</kbd>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {rawMode && !readOnly ? (
        <textarea
          ref={textareaRef}
          defaultValue={rawContent}
          className="w-full min-h-[20rem] px-4 py-3 bg-gray-900 text-gray-100 font-mono text-sm focus:outline-none resize-y border-0"
          spellCheck={false}
          onChange={(e) => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
              onChangeRef.current(e.target.value)
            }, 300)
          }}
        />
      ) : (
        <EditorContent editor={editor} />
      )}
    </div>
  )
})

export default WikiEditor
